import {fs} from '../../electron-modules';
import path from 'path';

export const readBufferChunks = (filePath, byteStart, chunkSize, maxChunks): Promise<any> => {
  return new Promise((resolve, reject) => {
    const debugMessages = false;
    const timeStart = performance.now();

    const stats = fs.statSync(filePath);
    const fileSizeInBytes = stats.size;

    // No more data can be found
    if (byteStart >= fileSizeInBytes) {
      resolve({buffers: []});
      return;
    }

    if (debugMessages) {
      console.log(`[BufferReader]: byte start: ${byteStart}`);
    }

    // Initialize our results buffer (slightly larger than required)
    let bytesRead = 0;
    let size = (maxChunks + 1) * chunkSize;
    let data = Buffer.alloc(size);

    /** Append file chunks to our data buffer when new data is received */
    const onDataEvent = chunk => {
      let buffer = chunk.slice(0, chunk.length);
      data.set(buffer, bytesRead);
      bytesRead += chunk.length;
    };

    /** Send our data to our viewer after finishing reading */
    const onEndEvent = () => {
      let response = {
        file: baseFileEvent(filePath),
        buffers: chunksFromBuffer(),
        bufferChunkSize: chunkSize,
        numBytes: fileSizeInBytes,
        maxchunks: maxChunks
      };

      if (debugMessages) {
        let timeEnd = performance.now();
        let timeElapsed = timeEnd - timeStart;
        timeElapsed = Math.round(timeElapsed * 100) / 100;
        console.log(`[BufferReader]: read file buffer: ${timeElapsed}ms`);
        console.log(response.buffers);
      }

      resolve(response);
    };

    /** Convert full buffer to arraybuffer chunks */
    const chunksFromBuffer = () => {
      let chunks = [];
      let buffer = data.slice(0, bytesRead).buffer;

      for (let i = 0; i < maxChunks; i++) {
        let start = i * chunkSize;
        if (start >= bytesRead) {
          break;
        }

        let finish = start + chunkSize;
        finish = Math.min(bytesRead, finish);
        let chunk = buffer.slice(start, finish);
        chunks.push(chunk);
      }

      return chunks;
    };

    const initReader = () => {
      const bytesToRead = maxChunks * chunkSize;
      let byteReadSize = 1024 * 1024 * 5;
      byteReadSize = Math.min(byteReadSize * 0.5, bytesToRead);

      fs.createReadStream(filePath, {
        highWaterMark: byteReadSize,
        start: byteStart,
        end: byteStart + bytesToRead
      }).on("end", () => {
        onEndEvent();
      }).on("data", chunk => {
        onDataEvent(chunk);
      });
    };

    initReader();
  });
};

export const readFileJSON = (path: string, staticFile=false): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (staticFile) {
      readJsonFromURL(resolve, reject, path);
    } else {
      readJsonElectron(resolve, reject, path);
    }
  });
};

export const readFileBuffer = (path: string, staticFile=false): Promise<any> => {
  return new Promise(((resolve, reject) => {
    if (staticFile) {
      readFileBufferFromURL(resolve, reject, path);
    } else {
      readFileBufferElectron(resolve, reject, path);
    }
  }));
};

export const baseFileEvent = filePath => ({
  name: path.basename(filePath),
  path: filePath,
  visible: true,
  local: false
});

const readJsonFromURL = (resolve, reject, path) => {
  fetch(path, {
    headers: {'Content-Type': 'application/json'}
  })
    .then(res => res.json())
    .then(data => {
      resolve(data);
    })
    .catch(() => {
      reject('Error reading file as json');
    });
};

const readJsonElectron = (resolve, reject, path) => {
  fs.readFile(path, 'utf-8', (err, data) => {
    if (err) {
      reject('Error reading file as json');
      return;
    }

    resolve(JSON.parse(data));
  });
};

const readFileBufferFromURL = (resolve, reject, path) => {
  fetch(path)
    .then(res => {
      if (res.status === 404) {
        throw new Error("File Not Found");
      }
      return res.arrayBuffer();
    })
    .then(data => {
      if (data.byteLength === 0) {
        throw new Error("File Not Found");
      }
      resolve(data);
    })
    .catch(() => {
      reject('Error reading file as arraybuffer');
    });
};

const readFileBufferElectron = (resolve, reject, path) => {
  fs.readFile(path, (err, data) => {
    if (err) {
      reject('Error reading file as arraybuffer');
      return;
    }

    resolve(data.buffer);
  });
};
